'use client';

import * as React from 'react';
import {
  FilmIcon,
  SparklesIcon,
  WandIcon,
  MusicIcon,
  VideoIcon,
  ClockIcon,
  LayersIcon,
  SettingsIcon
} from 'lucide-react';
import { cn } from '@workspace/ui/lib/utils';
import { useInView } from 'framer-motion';
import MuxPlayer from '@mux/mux-player-react';

export function InteractiveDemo(): React.JSX.Element {
  const [currentStep, setCurrentStep] = React.useState<'prompt' | 'processing' | 'preview'>('prompt');
  const [selectedPrompt, setSelectedPrompt] = React.useState<string>('');
  const [selectedVideo, setSelectedVideo] = React.useState<string>('');
  const [progress, setProgress] = React.useState(0);

  // Create ref for scroll detection
  const sectionRef = React.useRef<HTMLElement>(null);
  useInView(sectionRef, { once: true, margin: "-100px" });

  // Sample prompts for quick selection
  const samplePrompts = [
    {
      id: 'music-video',
      title: 'Gorah Music Video',
      prompt: 'Singer moves through warehouse as dancers emerge, props transform spaces. Gravity-defying performances on walls build to explosive finale.',
      duration: '2:00',
      style: 'Music Video',
      playbackId: 'mWVFse1EjBz3ZhiVz9B4NJMoup02rxRaD7smZnblbTD8'
    },
    {
      id: 'fashion-art',
      title: 'Fashion as Art',
      prompt: 'Model walks through minimalist space as outfit transforms - fabric flows like liquid metal with impossible silhouettes.',
      duration: '0:30',
      style: 'Fashion Art',
      playbackId: 'SFLiD2q02c8KrzbdKWXZvC4tsHBNqqWIIcvAsARxqNc4'
    },
    {
      id: 'paper-plane',
      title: 'Paper Plane Journey',
      prompt: 'An elderly person releases a paper plane from a rooftop that journeys through the city to land on a gravestone.',
      duration: '0:30',
      style: 'Emotional Drama',
      playbackId: 'sqwFTthf9peq4TnPU3oe3OwJrxqIEAElSWp9FADKh01E'
    },
    {
      id: 'last-light',
      title: 'Last Light',
      prompt: 'Astronaut Maya races back for her ill sister as Earth faces destruction. They face the end together, honoring a childhood promise.',
      duration: '1:00',
      style: 'Sci-Fi Drama',
      playbackId: 'mexeGV9lzn5f25mc5kpdEexHcDrSG01wMF9FQKumT8X8'
    }
  ];

  // Simulate processing when prompt is selected
  React.useEffect(() => {
    if (currentStep === 'processing') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setCurrentStep('preview');
            }, 500);
            return 100;
          }
          return prev + 2;
        });
      }, 50);

      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const handlePromptSelect = (prompt: typeof samplePrompts[0]) => {
    setSelectedPrompt(prompt.prompt);
    setSelectedVideo(prompt.playbackId);
    setProgress(0);
    setCurrentStep('processing');
    
    // Scroll to top of the demo section on mobile devices
    if (window.innerWidth < 1024) { // lg breakpoint
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Calculate number of scenes based on selected video duration
  const getSceneCount = () => {
    const selected = samplePrompts.find(p => p.playbackId === selectedVideo);
    if (!selected) return 4; // default
    const duration = parseInt(selected.duration.split(':')[0], 10) * 60 + parseInt(selected.duration.split(':')[1], 10);
    return Math.round(duration / 8); // 8 seconds per scene
  };

  const resetDemo = () => {
    setCurrentStep('prompt');
    setSelectedPrompt('');
    setSelectedVideo('');
    setProgress(0);
  };

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-[#0E0F1A]" id="interactive-demo">
      <div className="container px-6">
        <div className="mx-auto max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <FilmIcon className="h-5 w-5 text-[#00C6FF]" />
              <span className="text-sm font-medium text-[#00C6FF]">Interactive Demo</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              Watch AI create videos from your ideas
            </h2>
            <p className="text-lg lg:text-xl text-[#9EA1AE] max-w-2xl mx-auto leading-relaxed">
              Type any concept - music video, commercial, or film. See it come to life in minutes.
            </p>
          </div>

          {/* Demo Interface */}
          <div className="bg-[#1A1B29]/80 backdrop-blur-xl rounded-xl border border-[#4531FF]/20 shadow-lg overflow-hidden">
            {/* Progress Bar */}
            {currentStep === 'processing' && (
              <div className="bg-[#4531FF]/10 p-4 border-b border-[#4531FF]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">Creating your video...</span>
                  <span className="text-sm text-[#9EA1AE]">{progress}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#4531FF] to-[#00C6FF] transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex items-center gap-6 mt-3 text-xs text-[#9EA1AE]">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="h-3 w-3" />
                    <span>Generating scenes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <WandIcon className="h-3 w-3" />
                    <span>Applying effects</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MusicIcon className="h-3 w-3" />
                    <span>Adding soundtrack</span>
                  </div>
                </div>
              </div>
            )}

            <div className={cn(
              "lg:grid lg:grid-cols-3",
              currentStep === 'prompt' ? "flex flex-col-reverse" : "flex flex-col"
            )}>
              {/* Right Panel - Video Preview (appears first on mobile) */}
              <div className="lg:col-span-2 p-6">
                {currentStep === 'prompt' ? (
                  <div className="aspect-video bg-white/5 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <VideoIcon className="h-12 w-12 text-[#9EA1AE] mx-auto mb-4" />
                      <p className="text-[#9EA1AE]">
                        Your movie will appear here
                      </p>
                    </div>
                  </div>
                ) : currentStep === 'processing' ? (
                  <div className="aspect-video bg-white/5 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="relative">
                        <SparklesIcon className="h-12 w-12 text-[#00C6FF] mx-auto mb-4 animate-pulse" />
                        <div className="absolute inset-0 blur-xl bg-[#00C6FF]/20 animate-pulse" />
                      </div>
                      <p className="text-white font-medium mb-2">Filming your movie...</p>
                      <p className="text-sm text-[#9EA1AE]">
                        AI is creating scenes, characters, and cinematic effects
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Video Player */}
                    <div className="aspect-video bg-black rounded-lg relative overflow-hidden">
                      <MuxPlayer
                        playbackId={selectedVideo}
                        metadata={{
                          video_id: selectedVideo,
                          video_title: selectedPrompt,
                          viewer_user_id: "demo-user",
                        }}
                        streamType="on-demand"
                        autoPlay
                        muted
                        playsInline
                        onError={(error) => {
                          console.error('MuxPlayer error:', error);
                        }}
                      />
                    </div>

                    {/* Video Controls */}
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                        <LayersIcon className="h-5 w-5 text-[#9EA1AE]" />
                        <div>
                          <p className="text-sm font-medium text-white">{getSceneCount()} Scenes</p>
                          <p className="text-xs text-[#9EA1AE]">AI-generated shots</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                        <SettingsIcon className="h-5 w-5 text-[#9EA1AE]" />
                        <div>
                          <p className="text-sm font-medium text-white">4K Quality</p>
                          <p className="text-xs text-[#9EA1AE]">60fps rendering</p>
                        </div>
                      </div>
                    </div>

                    {/* Export Options */}
                    {/* <div className="mt-6 flex items-center gap-3">
                      <button className="flex-1 py-3 bg-gradient-to-r from-[#4531FF] to-[#00C6FF] text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                        <DownloadIcon className="h-4 w-4" />
                        Export Video
                      </button>
                      <button className="px-4 py-3 border border-[#4531FF]/20 rounded-lg hover:bg-white/10 transition-colors">
                        <ShareIcon className="h-4 w-4 text-white" />
                      </button>
                    </div> */}
                  </>
                )}
              </div>

              {/* Left Panel - Prompt Selection (appears second on mobile) */}
              <div className="lg:col-span-1 p-6 border-b lg:border-b-0 lg:border-r border-[#4531FF]/20">
                <h3 className="font-medium mb-4 text-white">Pick a video type</h3>

                <div className="space-y-3">
                  {samplePrompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptSelect(prompt)}
                      disabled={currentStep !== 'prompt'}
                      className={cn(
                        "w-full text-left p-4 rounded-lg border transition-all",
                        "hover:border-[#00C6FF] hover:shadow-sm",
                        selectedPrompt === prompt.prompt ? "border-[#00C6FF] bg-[#4531FF]/10" : "border-[#4531FF]/20",
                        currentStep !== 'prompt' && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-sm text-white">{prompt.title}</h4>
                        <div className="flex items-center gap-1 text-xs text-[#9EA1AE]">
                          <ClockIcon className="h-3 w-3" />
                          <span>{prompt.duration}</span>
                        </div>
                      </div>
                      <p className="text-xs text-[#9EA1AE] line-clamp-2 mb-2">
                        {prompt.prompt}
                      </p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-white/10 text-white">
                        {prompt.style}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-[#4531FF]/20">
                  <button
                    onClick={resetDemo}
                    disabled={currentStep === 'prompt'}
                    className={cn(
                      "w-full py-2 rounded-lg text-sm font-medium transition-all text-white",
                      "border border-[#4531FF]/20 hover:bg-white/10",
                      currentStep === 'prompt' && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    Reset Demo
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-lg mb-3">
                <ClockIcon className="h-6 w-6 text-[#00C6FF]" />
              </div>
              <p className="text-2xl font-medium text-white">2.5 min</p>
              <p className="text-sm text-[#9EA1AE]">Average creation time</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-lg mb-3">
                <SparklesIcon className="h-6 w-6 text-[#00C6FF]" />
              </div>
              <p className="text-2xl font-medium text-white">Any Genre</p>
              <p className="text-sm text-[#9EA1AE]">Drama, comedy, action</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-lg mb-3">
                <VideoIcon className="h-6 w-6 text-[#00C6FF]" />
              </div>
              <p className="text-2xl font-medium text-white">Pro Quality</p>
              <p className="text-sm text-[#9EA1AE]">Cinema-ready films</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}